import React, { useEffect, Fragment, useCallback, useRef } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import { useAppSelector, useAppDispatch } from 'store';
import { showResponse } from 'store/actions/response';
import { withAuth } from 'config';
import Layout from 'components/UI/Layout';
import PostsToolbar from 'components/Shared/PostsToolbar';
import ScreenTitle from 'components/Shared/ScreenTitle';

import Post from '../Post';
import AdBlock from './components/AdBlock';
import { PostProps } from './model';
import { useLanguage } from 'languages/languageContext';
import { getAllAds, getPosts } from 'store/reducers/news';
import { ScrollToTopButton } from 'components/UI/ScrollToTop/ScrollToTopButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import { getConfig } from 'config/config';

const { theme } = getConfig();

interface Props extends RouteComponentProps<any>, PostProps {}

const Posts: React.FC<Props> = (props) => {
  const { screenTitles } = useLanguage();
  const dispatch = useAppDispatch();
  const { ads, feed } = useAppSelector((state) => state.news);
  const { news, loading, nextCursor, pinPostLeft } = feed;
  const isGuest = useAppSelector((state) => state.users.user?.isGuest);

  const guestNoticeShownRef = useRef(false);
  const initialFetchRequestedRef = useRef(false);

  useEffect(() => {
    if (!ads.ads?.length) {
      dispatch(getAllAds());
    }
  }, []);

  useEffect(() => {
    if (initialFetchRequestedRef.current) return;

    if (news.length) {
      initialFetchRequestedRef.current = true;
      return;
    }

    initialFetchRequestedRef.current = true;
    dispatch(getPosts({ nextCursor: '', pinPostLeft }));
  }, [dispatch, news.length]);

  const handleScrollableEnd = useCallback(() => {
    if (!guestNoticeShownRef.current && isGuest && news.length >= 15) {
      guestNoticeShownRef.current = true;
      dispatch(showResponse({ message: 'Ältere Beiträge sind nur für Mitglieder einsehbar.' }));
    }
  }, [dispatch, isGuest, news.length]);

  useEffect(() => {
    const handleScroll = () => {
      if (loading) return;

      const { scrollHeight } = document.documentElement;
      const scrollTop = window.scrollY;
      const clientHeight = window.innerHeight;
      const isNearBottom = scrollHeight - (scrollTop + clientHeight) < 200;

      if (!isNearBottom) return;

      if (nextCursor == null) {
        if (news.length) {
          handleScrollableEnd();
        }
        return;
      }

      if (typeof nextCursor === 'string') {
        dispatch(getPosts({ nextCursor, pinPostLeft }));
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [dispatch, handleScrollableEnd, loading, nextCursor, news.length]);

  const renderItem = useCallback(
    (post, idx) => {
      if (ads?.ads?.length) {
        if (idx === 0 || (idx !== 0 && idx % 6 === 0)) {
          let adIndex;
          if (idx === 0) adIndex = 0;
          else adIndex = (idx / 6) % ads.ads.length;

          return (
            <Fragment key={`ad_post_${idx}`}>
              <Post isOwnerPosts={true} post={post} key={post?._id} />
              <AdBlock key={`ad-${post._id}-${idx}`} item={ads.ads[adIndex]} />
            </Fragment>
          );
        }
      }
      return <Post isOwnerPosts={true} post={post} key={post?._id} />;
    },
    [ads.ads],
  );

  return (
    <React.Fragment>
      <Layout backgroundColor={news.length > 0 ? theme.BACKGROUND_SECONDARY : theme.BACKGROUND}>
        <ScreenTitle title={screenTitles.postsTitle} />
        <div className={'mb-10'}>
          <div className={'no-scrollbar'}>
            {news.map((post, i) => post && renderItem(post, i))}
            {loading && (
              <div style={{ textAlign: 'center' }} key="loading-indicator">
                <CircularProgress style={{ color: theme.BUTTON_SECONDARY }} size={25} />
              </div>
            )}
            <div className="h-24" />
          </div>

          <div>
            <PostsToolbar toolbarType="post" />
          </div>
        </div>
      </Layout>
      <ScrollToTopButton />
    </React.Fragment>
  );
};

export default withAuth(Posts);
